import type { RankingResponse, SystemResponse } from "@/shared/api";

type Source<T> =
  | { readonly status: "ready"; readonly data: T }
  | { readonly status: "loading" | "forbidden" | "unavailable" | "unreadable" };

export type OverviewSources = {
  readonly ranking: Source<Pick<RankingResponse, "state" | "generated_at" | "market_data_cutoff" | "market_snapshot_sha256">>;
  readonly system: Source<Pick<SystemResponse, "as_of" | "reconciliation" | "findings">>;
};

export type SourceVerdict = {
  readonly value: string;
  readonly tone: "good" | "warning" | "muted";
  readonly detail: string;
};

function unavailable(source: Source<unknown> | undefined): SourceVerdict | null {
  if (source?.status === "ready") return null;
  const value = source === undefined ? "НЕТ ИСТОЧНИКА"
    : source.status === "loading" ? "ЧТЕНИЕ…"
    : source.status === "forbidden" ? "НЕТ ДОСТУПА" : "НЕДОСТУПНО";
  return { value, tone: "muted", detail: "Чтение источника не получено; состояние не подтверждено." };
}

function timestamp(value: string | null | undefined): value is string {
  return value != null && Number.isFinite(Date.parse(value));
}

export function rankingVerdict(source: OverviewSources["ranking"] | undefined): SourceVerdict {
  const missing = unavailable(source);
  if (missing !== null) return missing;
  if (source?.status !== "ready") throw new Error("unreachable source state");
  const data = source.data;
  // The publisher decides freshness. Account snapshot freshness is unrelated.
  const complete = timestamp(data.market_data_cutoff) && timestamp(data.generated_at)
    && /^[a-f0-9]{64}$/i.test(data.market_snapshot_sha256 ?? "");
  const value = data.state === "NO_DATA_YET" ? "НЕТ НАБЛЮДЕНИЯ"
    : !complete ? "НЕПОЛНЫЙ ИСТОЧНИК"
    : data.state === "STALE" ? "УСТАРЕЛО"
    : "АКТУАЛЬНО";
  return {
    value,
    tone: value === "АКТУАЛЬНО" ? "good" : "warning",
    detail: `Cutoff: ${data.market_data_cutoff ?? "нет"}; записано: ${data.generated_at ?? "нет"}; snapshot SHA-256: ${data.market_snapshot_sha256 ?? "нет"}. Это наблюдение ранжирования, не здоровье всех фидов/листенеров.`,
  };
}

/** Up to three coins by name, the rest counted. */
function coins(symbols: readonly string[]): string {
  const named = symbols.slice(0, 3).map((symbol) => symbol.replace(/USDT$/, ""));
  return symbols.length > 3 ? `${named.join(", ")} +${String(symbols.length - 3)}` : named.join(", ");
}

export function reconciliationVerdict(source: OverviewSources["system"] | undefined): SourceVerdict {
  const missing = unavailable(source);
  if (missing !== null) return missing;
  if (source?.status !== "ready") throw new Error("unreachable source state");
  const { reconciliation: data, findings, as_of: asOf } = source.data;
  // A reconciliation merely running is the ordinary state of a working bot,
  // not a fault: only a divergence, an interrupted attempt or a stale result
  // is named, and by the coins it concerns.
  const value = !timestamp(data.reconciled_through) || !timestamp(asOf) ? "НЕТ НАБЛЮДЕНИЯ"
    : findings.includes("RECONCILIATION_NEVER_RAN") ? "НЕТ НАБЛЮДЕНИЯ"
    : data.divergent.length > 0 ? `РАСХОЖДЕНИЕ — ${coins(data.divergent)}`
    : data.interrupted.length > 0 ? `ПРЕРВАНО — ${coins(data.interrupted)}`
    : data.stale.length > 0 ? `УСТАРЕЛО — ${coins(data.stale)}`
    : findings.includes("RECONCILIATION_STALE") ? "УСТАРЕЛО"
    : data.incomplete > 0 || findings.includes("RECONCILIATION_INCOMPLETE") ? "НЕПОЛНОЕ"
    : data.cycles_tracked === 0 ? "НЕТ ЦИКЛОВ"
    : data.updating > 0 ? `АКТУАЛЬНО · обновляется: ${String(data.updating)}`
    : "АКТУАЛЬНО";
  return {
    value,
    tone: value.startsWith("АКТУАЛЬНО") ? "good" : "warning",
    detail: `Источник: account reconciliation; последняя запись сверки: ${data.reconciled_through ?? "нет"}; чтение: ${asOf}; записей сверки (включая закрытые циклы): ${String(data.cycles_tracked)}; открытых циклов с проблемой: ${String(data.incomplete)} (расхождение: ${String(data.divergent.length)}, прервано: ${String(data.interrupted.length)}, устарело более 20 мин: ${String(data.stale.length)}); обновляется сейчас: ${String(data.updating)}. Свежесть последней записи не доказывает свежесть каждого цикла и не доказывает отсутствие gaps; причины показаны отдельно.`,
  };
}

export function sourceIssues(sources: OverviewSources | undefined): readonly string[] {
  if (sources === undefined) return [];
  return [
    { label: "Наблюдение ранжирования", verdict: rankingVerdict(sources.ranking) },
    { label: "Сверка", verdict: reconciliationVerdict(sources.system) },
  ].filter(({ verdict }) => verdict.tone !== "good")
    .map(({ label, verdict }) => `${label}: ${verdict.value}`);
}
